import { editorInteractionStateTeamUuidAtom } from '@/app/atoms/editorInteractionStateAtom';
import { events } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import { supportedFileTypesFromGrid } from '@/app/helpers/files';
import { uploadImageToCell } from '@/app/helpers/imageUpload';
import { useFileImport } from '@/app/ui/hooks/useFileImport';
import { pickFilesFromDrive } from '@/shared/components/DriveFilePicker/driveFilePicker';
import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';

export const IMAGE_INPUT_ID = 'global-image-input-element';

const SUPPORTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

// Maximum dimension (width or height) for newly inserted images in pixels
const MAX_IMAGE_DIMENSION = 600;

/**
 * Calculates the number of columns and rows an image should span based on its pixel dimensions.
 * Uses the target sheet's offsets to determine cell sizes. Images are scaled down to fit within MAX_IMAGE_DIMENSION.
 */
export function calculateImageCellSize(
  startColumn: number,
  startRow: number,
  imageWidth: number,
  imageHeight: number,
  sheetId: string
): { width: number; height: number } {
  // Scale down if either dimension exceeds the maximum
  const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(imageWidth, imageHeight));
  const scaledWidth = imageWidth * scale;
  const scaledHeight = imageHeight * scale;
  const offsets = sheets.getById(sheetId)?.offsets ?? sheets.sheet.offsets;

  // Calculate columns needed. If a column has zero/negative size (hidden column),
  // we stop early and use the current width rather than spanning hidden columns.
  let width = 1;
  let accumulatedWidth = 0;
  let col = startColumn;
  while (accumulatedWidth < scaledWidth) {
    const placement = offsets.getColumnPlacement(col);
    if (placement.size <= 0) break;
    accumulatedWidth += placement.size;
    if (accumulatedWidth < scaledWidth) {
      width++;
      col++;
    }
  }

  // Calculate rows needed. Same logic as columns for hidden rows.
  let height = 1;
  let accumulatedHeight = 0;
  let row = startRow;
  while (accumulatedHeight < scaledHeight) {
    const placement = offsets.getRowPlacement(row);
    if (placement.size <= 0) break;
    accumulatedHeight += placement.size;
    if (accumulatedHeight < scaledHeight) {
      height++;
      row++;
    }
  }

  return { width, height };
}

/**
 * This component handles file inserts for the grid. Insert > File
 * (`insertActionsSpec[Action.InsertFile].run()`) emits `insertFileFromPicker`,
 * which this component turns into the Drive file picker and an import at the
 * cursor. The hidden image input is still used for image-into-cell uploads.
 */
export function GridFileInput() {
  const handleFileImport = useFileImport();
  const imageInputRef = useRef<HTMLInputElement>(null);
  const teamUuid = useAtomValue(editorInteractionStateTeamUuidAtom);

  useEffect(() => {
    const handleInsertFile = async () => {
      // Captured before the picker opens so the import lands where the cursor
      // was when Insert → File was chosen, not wherever it ends up afterwards.
      const sheetId = sheets.current;
      const position = sheets.sheet.cursor.position;
      const insertAt = { x: position.x, y: position.y };
      const cursor = sheets.getCursorPosition();
      const files = await pickFilesFromDrive({
        title: 'Import a file',
        accept: supportedFileTypesFromGrid,
        multiple: true,
        source: 'insertFile',
      });
      if (files.length > 0) {
        handleFileImport({
          files,
          sheetId,
          insertAt,
          cursor,
          teamUuid,
        });
      }
    };
    events.on('insertFileFromPicker', handleInsertFile);
    return () => {
      events.off('insertFileFromPicker', handleInsertFile);
    };
  }, [handleFileImport, teamUuid]);

  const handleImageUpload = (file: File) => {
    const cursor = sheets.sheet.cursor.position;
    const sheetId = sheets.current;
    uploadImageToCell(file, sheetId, cursor.x, cursor.y);
  };

  return (
    <>
      <input
        id={IMAGE_INPUT_ID}
        ref={imageInputRef}
        type="file"
        hidden
        accept={SUPPORTED_IMAGE_TYPES.join(',')}
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length > 0) {
            handleImageUpload(files[0]);
          }
        }}
      />
    </>
  );
}
